import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';

/** Action label of the thinking step, shared by the live stream and history restore. */
export const THINKING_STEP_ACTION = 'Thinking';

/**
 * Engaging Loader Step Interface
 */
export interface EngagingLoaderStep {
  action?: string;
  reasoning: string;
  is_complete?: boolean;
  thread_id?: string;
  timestamp?: string;
  type?: 'planning' | 'searching' | 'reviewing' | 'analyzing' | 'crafting';
  searches?: string[];
  sources?: { title: string; url: string; domain: string }[];
}

/**
 * Engaging Loader Component
 * 
 * Shows actions and reasoning above the response during streaming,
 * then converts to collapsible summary after completion.
 * 
 * Design:
 * - Collapsed (Default): Shows action text with caret (^)
 * - Expanded: Shows action text with down caret (v) and reasoning below
 * - Timeline style with circles and connecting lines
 */
@Component({
  selector: 'app-agent-engaging-loader',
  templateUrl: './engaging-loader.component.html',
  styleUrls: ['./engaging-loader.component.scss']
})
export class AgentEngagingLoaderComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isStreaming: boolean = false;
  @Input() isCompleted: boolean = false;
  @Input() reasoningSteps: EngagingLoaderStep[] = [];
  @Input() currentAction: string = '';
  
  // Track manually expanded steps by user
  expandedSteps: Set<number> = new Set();
  // Track manually collapsed steps by user (to override auto-expand)
  collapsedSteps: Set<number> = new Set();
  // Track previous steps count to detect new steps
  private previousStepsCount: number = 0;

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    // Reset when streaming starts again
    if (changes['isStreaming'] && this.isStreaming) {
      this.expandedSteps.clear();
      this.collapsedSteps.clear();
      this.previousStepsCount = 0;
    }

    // When completed (message streaming started), collapse all steps
    if (changes['isCompleted'] && this.isCompleted) {
      this.expandedSteps.clear();
      // Add all steps to collapsed set so they stay collapsed
      for (let i = 0; i < this.reasoningSteps.length; i++) {
        this.collapsedSteps.add(i);
      }
    }

    // Detect when a new step is added
    if (changes['reasoningSteps'] && this.reasoningSteps) {
      const currentCount = this.reasoningSteps.length;
      
      if (currentCount > this.previousStepsCount && currentCount > 0) {
        // New step added - collapse previous step, expand new one
        this.expandedSteps.clear();
        this.collapsedSteps.clear();
        // Collapse all previous steps
        for (let i = 0; i < currentCount - 1; i++) {
          this.collapsedSteps.add(i);
        }
      }
      
      this.previousStepsCount = currentCount;
    }
  }

  /**
   * Toggle individual step expanded/collapsed state
   */
  toggleStepExpanded(index: number): void {
    if (this.isStepExpanded(index)) {
      // Currently expanded, collapse it
      this.expandedSteps.delete(index);
      this.collapsedSteps.add(index);
    } else {
      // Currently collapsed, expand it
      this.collapsedSteps.delete(index);
      this.expandedSteps.add(index);
    }
  }

  /**
   * Check if a specific step is expanded
   * - User's manual toggle always takes priority
   * - During streaming: only the latest step is auto-expanded (if not manually collapsed)
   * - After completion: all collapsed by default
   */
  isStepExpanded(index: number): boolean {
    // User manually collapsed - always respect
    if (this.collapsedSteps.has(index)) {
      return false;
    }
    
    // User manually expanded - always respect
    if (this.expandedSteps.has(index)) {
      return true;
    }
    
    // During streaming (before completion), auto-expand only the latest step
    if (this.isStreaming && !this.isCompleted) {
      return index === this.reasoningSteps.length - 1;
    }
    
    return false;
  }

  /**
   * Get truncated action text for collapsed view
   */
  getTruncatedAction(action: string, maxLength: number = 40): string {
    if (!action) return 'Processing...';
    if (action.length <= maxLength) return action;
    return action.substring(0, maxLength) + '..';
  }

  /**
   * Check if action text is truncated
   * Used to determine if tooltip should be shown
   */
  isActionTruncated(action: string, maxLength: number = 40): boolean {
    if (!action) return false;
    return action.length > maxLength;
  }

  /**
   * Get current phase description for streaming
   */
  getCurrentPhaseDescription(): string {
    if (this.currentAction) {
      return this.currentAction;
    }
    if (this.reasoningSteps.length > 0) {
      return `Processing • ${this.reasoningSteps.length} step${this.reasoningSteps.length > 1 ? 's' : ''}`;
    }
    return 'Working';
  }

  ngOnDestroy(): void {}
}
